// Zahlen und Daten im deutschen Format (README „Sprache und Ton“):
// 12.500 €, 4.800,00 €, 14.10.2026, 09:30.
//
// Alle Formatierer nehmen auch, was eine API leer liefert: `null`,
// `undefined` und `''` werden „–“. Ein Wert, der sich nicht lesen lässt,
// erscheint so, wie er kam, statt die Seite mit einem RangeError abzubrechen.

/** Was alle Formatierer für einen leeren Wert zeigen; `Angaben` erkennt es. */
export const LEER = '–';

const euroGanz = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const euroCent = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
const zahl = new Intl.NumberFormat('de-DE');
const datum = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const datumLang = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const zeit = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });
const datumZeit = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

type Datumswert = Date | string | null | undefined;

/** Datum aus `Date` oder ISO-Text; `null` bei leer, der Rohtext bei Unlesbarem. */
function alsDatum(d: Datumswert): Date | string | null {
  if (d === null || d === undefined || d === '') return null;
  // Ein reines Datum („2026-10-14“) ist ein Kalendertag, kein Zeitpunkt:
  // lokal lesen, nicht als Mitternacht UTC.
  const tag = typeof d === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(d) : null;
  const wert = tag ? new Date(Number(tag[1]), Number(tag[2]) - 1, Number(tag[3])) : typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(wert.getTime())) return typeof d === 'string' ? d : LEER;
  return wert;
}

/**
 * `12.500 €` (ganze Euro) oder mit `cent: true` `4.800,00 €`. Nimmt auch
 * Decimal-Texte, wie die APIs Beträge liefern (`"1234.50"`).
 */
export function formatEuro(betrag: number | string | null | undefined, { cent = false }: { cent?: boolean } = {}) {
  if (betrag === null || betrag === undefined || (typeof betrag === 'string' && betrag.trim() === '')) return LEER;
  const wert = typeof betrag === 'string' ? Number(betrag) : betrag;
  if (Number.isNaN(wert)) return typeof betrag === 'string' ? betrag : LEER;
  // −0 (etwa aus einer Rundung) als „0 €“, nicht „-0 €“.
  return (cent ? euroCent : euroGanz).format(wert === 0 ? 0 : wert);
}

/**
 * Euro kompakt für Diagrammachsen und Linienbeschriftungen: `190k`,
 * `1,2 Mio`, `-40k`, unter tausend die Zahl. Nur dort, wo der Platz fehlt; im
 * Text und in Kacheln `formatEuro`.
 */
export function formatEuroAchse(betrag: number) {
  if (Number.isNaN(betrag)) return LEER;
  const absolut = Math.abs(betrag);
  const vorzeichen = betrag < 0 ? '-' : '';
  // Gerundet entscheiden, nicht roh: 999.600 € sind „1 Mio“, nicht „1000k“.
  if (Math.round(absolut / 1_000) >= 1_000) return `${vorzeichen}${formatZahl(absolut / 1_000_000, { stellen: 1 })} Mio`;
  if (Math.round(absolut) >= 1_000) return `${vorzeichen}${Math.round(absolut / 1_000)}k`;
  return `${vorzeichen}${Math.round(absolut)}`;
}

const zahlStellen = new Map<string, Intl.NumberFormat>();

/**
 * `12.500` oder `2.480,5`. Mit `stellen` höchstens so viele Nachkommastellen
 * (Fläche in m², Dateigröße in MB), sonst die Voreinstellung (bis zu drei).
 * Mit `fest: true` genau so viele, auch Nullen: `12,0 %`, `3,5 h` in
 * Spalten, die untereinander stehen.
 */
export function formatZahl(wert: number | null | undefined, { stellen, fest = false }: { stellen?: number; fest?: boolean } = {}) {
  if (wert === null || wert === undefined || Number.isNaN(wert)) return LEER;
  if (stellen === undefined) return zahl.format(wert);
  const schluessel = `${stellen}${fest ? 'f' : ''}`;
  let f = zahlStellen.get(schluessel);
  if (!f) {
    f = new Intl.NumberFormat('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: fest ? stellen : 0 });
    zahlStellen.set(schluessel, f);
  }
  return f.format(wert);
}

/** `14.10.2026`; mit `lang: true` `Mittwoch, 14. Oktober 2026` (Kopf einer Detailseite). */
export function formatDatum(d: Datumswert, { lang = false }: { lang?: boolean } = {}) {
  const wert = alsDatum(d);
  if (wert === null) return LEER;
  return typeof wert === 'string' ? wert : (lang ? datumLang : datum).format(wert);
}

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/**
 * `August 2026` für einen Monat, wie Projektionen ihn liefern (`"2026-08"`),
 * oder aus einem Datum (`"2026-08-14"`, `Date`). Mit `kurz: true` `Aug 26` für
 * Diagrammachsen.
 */
export function formatMonat(m: Date | string | null | undefined, { kurz = false }: { kurz?: boolean } = {}) {
  if (m === null || m === undefined || m === '') return LEER;
  let jahr: number;
  let monat: number;
  if (typeof m === 'string') {
    const t = /^(\d{4})-(\d{2})/.exec(m);
    if (!t) return m;
    jahr = Number(t[1]);
    monat = Number(t[2]) - 1;
  } else {
    if (Number.isNaN(m.getTime())) return LEER;
    jahr = m.getFullYear();
    monat = m.getMonth();
  }
  if (monat < 0 || monat > 11) return typeof m === 'string' ? m : LEER;
  return kurz ? `${MONATE_KURZ[monat]} ${String(jahr).slice(2)}` : `${MONATE[monat]} ${jahr}`;
}

/** `09:30` */
export function formatZeit(d: Datumswert) {
  const wert = alsDatum(d);
  if (wert === null) return LEER;
  return typeof wert === 'string' ? wert : zeit.format(wert);
}

/** `14.10.2026, 09:30` */
export function formatDatumZeit(d: Datumswert) {
  const wert = alsDatum(d);
  if (wert === null) return LEER;
  return typeof wert === 'string' ? wert : datumZeit.format(wert);
}

/** `820 KB`, `1,5 MB`, `2,1 GB` (Basis 1024, wie Browser und Betriebssysteme Uploads zeigen). */
export function formatDateigroesse(bytes: number | null | undefined) {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return LEER;
  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;
  // Gerundet entscheiden, nicht roh: 1.048.575 Bytes sind „1 MB“, nicht „1024 KB“.
  if (Math.round((bytes / mb) * 10) >= 10240) return `${formatZahl(bytes / gb, { stellen: 1 })} GB`;
  if (Math.round(bytes / kb) >= 1024) return `${formatZahl(bytes / mb, { stellen: 1 })} MB`;
  // Unter einem KB „1 KB“ statt „0 KB“: Die Datei ist ja da. Nur leer ist 0.
  return `${bytes <= 0 ? 0 : Math.max(1, Math.round(bytes / kb))} KB`;
}

const MINUTE = 60_000;
const STUNDE = 60 * MINUTE;

/** Kalendertage zwischen zwei Zeitpunkten in lokaler Zeit (gestern = 1). */
function kalendertage(von: Date, bis: Date) {
  const a = new Date(von.getFullYear(), von.getMonth(), von.getDate());
  const b = new Date(bis.getFullYear(), bis.getMonth(), bis.getDate());
  return Math.round((b.getTime() - a.getTime()) / (24 * STUNDE));
}

/**
 * Relative Zeit in einem Wortlaut für alle Apps: „gerade eben“, „vor 3
 * Minuten“, „vor 2 Stunden“ (am selben Tag), „gestern“, „vor 4 Tagen“, ab 7
 * Tagen das Datum. Mit `seit: true` für Dauern: „seit 2 Stunden“, „seit
 * gestern“, „seit 12 Tagen“ — eine Dauer bleibt eine Zahl, kein Datum, das man
 * selbst nachrechnen müsste. `jetzt` nur für Tests.
 */
export function formatVor(d: Datumswert, { seit = false, jetzt = new Date() }: { seit?: boolean; jetzt?: Date } = {}) {
  const wert = alsDatum(d);
  if (wert === null) return LEER;
  if (typeof wert === 'string') return wert;

  const vor = seit ? 'seit' : 'vor';
  const abstand = jetzt.getTime() - wert.getTime();
  // Leicht in der Zukunft (Uhren von Server und Gerät gehen nie gleich) zählt als jetzt.
  if (abstand < MINUTE) {
    if (abstand > -5 * MINUTE) return seit ? 'seit eben' : 'gerade eben';
    return formatDatum(wert);
  }
  if (abstand < STUNDE) {
    const n = Math.floor(abstand / MINUTE);
    return `${vor} ${n} ${n === 1 ? 'Minute' : 'Minuten'}`;
  }
  const tage = kalendertage(wert, jetzt);
  if (tage === 0) {
    const n = Math.floor(abstand / STUNDE);
    return `${vor} ${n} ${n === 1 ? 'Stunde' : 'Stunden'}`;
  }
  if (tage === 1) return seit ? 'seit gestern' : 'gestern';
  if (tage < 7 || seit) return `${vor} ${tage} Tagen`;
  return formatDatum(wert);
}
